import { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, Button, Chip,
  Table, TableHead, TableBody, TableRow, TableCell, CircularProgress, Alert, Tooltip,
} from '@mui/material';
import { ArrowBack as BackIcon, OpenInNew as OpenIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';
import api from '../../config/api';
import { formatDate } from '../../erp/formatDate';
import AtsBackgroundCheckSection from './AtsBackgroundCheckSection';
import {
  BACKGROUND_CHECK_POINTS, formatCheckPoints, formatPointsBalance, pointsBlockMessage, pointsBlockFromError,
} from './backgroundCheckPricing';

/**
 * Modal de la etapa "Verificación de antecedentes" (PLAN_MASIVO_PORTAL, fase 1).
 * Lista los postulantes de la etapa con el estado de sus antecedentes:
 * "Verificar" por fila (o "Reintentar" / "Actualizar"), "Ver detalle" si ya
 * hay consulta, y "Verificar pendientes" en lote con el costo antes de
 * confirmar (el lote se arma aquí, secuencial, una llamada por postulante).
 * Filtros (Todos / Sin consultar / Consultados / Con error) y "Volver a
 * consultar" por fila o en lote para los ya consultados: consulta nueva en
 * Chapi con el costo antes de confirmar (2026-10-13).
 * El backend decide si una ficha es consultable y reutiliza los checks de
 * menos de 30 días (no se cobran de nuevo).
 * Puntos (2026-11-01, PLAN_COBRO_ANTECEDENTES): cada consulta que trae un
 * informe nuevo cuesta 0,5 puntos. La nota muestra el precio y el saldo, los
 * botones lo dicen en su tooltip, la confirmación del lote suma el máximo y
 * el lote se detiene si se acaban los puntos (403 del backend).
 */

const TONES = {
  green: { color: '#166534', bg: '#DCFCE7' },
  yellow: { color: '#92400E', bg: '#FEF3C7' },
  red: { color: '#991B1B', bg: '#FEE2E2' },
  gray: { color: '#374151', bg: '#F3F4F6' },
};
const STATUS_TONE = {
  none: 'gray', processing: 'gray', not_found: 'gray', notConsultable: 'gray',
  clean: 'green', flagged: 'yellow', error: 'red',
};
const chipSx = (tone) => ({ backgroundColor: TONES[tone].bg, color: TONES[tone].color, fontWeight: 700, height: 22, fontSize: 11 });

const rowStatus = (row) => {
  const check = row.check;
  if (!check) return row.consultable ? 'none' : 'notConsultable';
  if (check.status === 'processing') return 'processing';
  if (check.status === 'error') return 'error';
  if (check.status === 'not_found') return 'not_found';
  return check.flagged ? 'flagged' : 'clean';
};

// Qué acción de consulta corresponde (null = ninguna)
const verifyAction = (row) => {
  if (!row.consultable) return null;
  const check = row.check;
  if (!check) return 'verify';
  if (check.status === 'error') return 'retry';
  if (!check.reusable) return 'refresh';
  return null;
};

// "Pendientes" del lote: nunca consultados o con error (los vencidos no)
const isPending = (row) => {
  const action = verifyAction(row);
  return action === 'verify' || action === 'retry';
};

// Ya consultados (terminado o sin datos): "Volver a consultar" hace una
// consulta nueva en Chapi aunque haya una reciente (2026-10-13)
const isRecheckable = (row) => Boolean(row.consultable && row.check
  && ['finished', 'not_found'].includes(row.check.status));

// Filtros de la lista (2026-10-13)
const FILTERS = ['all', 'none', 'consulted', 'error'];
const matchesFilter = (row, filter) => {
  if (filter === 'none') return !row.check;
  if (filter === 'consulted') return Boolean(row.check) && row.check.status !== 'error';
  if (filter === 'error') return row.check?.status === 'error';
  return true;
};

const AtsBackgroundCheckDialog = ({
  open,
  onClose,
  stage,
  applications = [],
  positionId,
  canEdit = false,
  onApplicationPatch,
  onOpenApplication,
}) => {
  const { t, i18n } = useTranslation();
  const tb = (key, opts) => t(`ats.backgroundCheckStage.${key}`, opts);
  const { authToken, showAlert } = useStore.getState();
  const headers = { Authorization: `Bearer ${authToken}` };
  // "Volver a consultar" (consulta nueva en el proveedor aunque haya una
  // reciente): quien puede editar la vacante (antes solo super admin)

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [busy, setBusy] = useState({});
  const [detail, setDetail] = useState(null); // { row, check, canRetry, loading }
  const [confirmForce, setConfirmForce] = useState(false);
  // { phase: 'confirm'|'running'|'done', mode: 'pending'|'recheck', queue, total, done, ok, failed }
  const [bulk, setBulk] = useState(null);
  const [filter, setFilter] = useState('all');
  // Puntos (2026-11-01): precio por consulta y saldo de la organización que
  // devuelve el resumen ({ canPerform, code, required, remaining, unlimited });
  // un 403 por puntos lo actualiza y bloquea los botones que consultan
  const [pricePoints, setPricePoints] = useState(BACKGROUND_CHECK_POINTS);
  const [pointsInfo, setPointsInfo] = useState(null);
  const pts = (n) => formatCheckPoints(n, i18n.language);
  const bal = (n) => formatPointsBalance(n, i18n.language);
  const pointsBlocked = Boolean(pointsInfo && pointsInfo.canPerform === false);
  const pointsBlockedText = pointsBlocked ? pointsBlockMessage(t, pointsInfo) : '';

  const applicationIds = applications.map((a) => a.uid || a.id).filter(Boolean);
  const idsKey = applicationIds.join(',');

  const loadRows = useCallback(async () => {
    if (applicationIds.length === 0) {
      setRows([]);
      return;
    }
    setLoading(true);
    setLoadError(false);
    try {
      const { data } = await api.post(
        `/captation/background-checks/summary`,
        { positionId, applicationIds },
        { headers }
      );
      setRows(data.response?.data || []);
      setPointsInfo(data.response?.points || null);
      if (typeof data.response?.pricing?.points === 'number') setPricePoints(data.response.pricing.points);
    } catch (err) {
      console.error('Error cargando antecedentes de la etapa:', err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionId, idsKey]);

  useEffect(() => {
    if (!open) return;
    setDetail(null);
    setBulk(null);
    setFilter('all');
    loadRows();
  }, [open, loadRows]);

  // Verifica una postulación. Devuelve { status: 'ok' | 'failed' | 'points',
  // block? }: 'points' = no se consultó por falta de puntos o del servicio.
  // force: consulta nueva aunque haya una reciente ("Volver a consultar")
  const verifyRow = async (row, { force = false } = {}) => {
    const id = row.applicationId;
    setBusy((prev) => ({ ...prev, [id]: true }));
    try {
      const { data } = await api.post(
        `/captation/background-checks/application/${id}`,
        force ? { force: true } : {},
        { headers }
      );
      const response = data.response || {};
      if (response.data) {
        setRows((prev) => prev.map((r) => (r.applicationId === id ? response.data : r)));
      }
      if (response.backgroundReview && onApplicationPatch) {
        onApplicationPatch(id, { backgroundReview: response.backgroundReview });
      }
      setDetail((prev) => (prev && prev.row.applicationId === id
        ? { ...prev, row: response.data || prev.row, check: response.check || prev.check, canRetry: false }
        : prev));
      // Saldo a la vista: se descuenta lo cobrado (lo vigente lo decide el backend)
      const charged = Number(response.check?.billing?.points) || 0;
      if (charged > 0 && !response.reused) {
        setPointsInfo((prev) => (prev && !prev.unlimited && typeof prev.remaining === 'number'
          ? { ...prev, remaining: prev.remaining - charged }
          : prev));
      }
      return { status: !response.check || response.check.status !== 'error' ? 'ok' : 'failed' };
    } catch (err) {
      console.error('Error verificando antecedentes:', err);
      // Sin puntos o sin el servicio: no se consultó y no es un problema de la
      // ficha, así que la fila sigue consultable
      const block = pointsBlockFromError(err);
      if (block) {
        setPointsInfo((prev) => ({ ...(prev || {}), ...block, canPerform: false }));
        return { status: 'points', block };
      }
      const reason = err.response?.data?.response?.reason;
      if (reason) setRows((prev) => prev.map((r) => (r.applicationId === id ? { ...r, consultable: false, reason } : r)));
      return { status: 'failed' };
    } finally {
      setBusy((prev) => ({ ...prev, [id]: false }));
    }
  };

  const handleVerifyOne = async (row) => {
    const result = await verifyRow(row);
    if (result.status === 'points') showAlert(pointsBlockMessage(t, result.block), 'warning');
    else if (result.status !== 'ok') showAlert(tb('verifyError'), 'error');
  };

  const handleForceRecheck = async (row) => {
    const result = await verifyRow(row, { force: true });
    setConfirmForce(false);
    if (result.status === 'points') showAlert(pointsBlockMessage(t, result.block), 'warning');
    else showAlert(result.status === 'ok' ? tb('forceRecheckDone') : tb('verifyError'), result.status === 'ok' ? 'success' : 'error');
  };

  const openDetail = async (row) => {
    setConfirmForce(false);
    setDetail({ row, check: null, canRetry: false, loading: true });
    try {
      const { data } = await api.get(
        `/captation/background-check/${row.applicantId}?applicationId=${row.applicationId}`,
        { headers }
      );
      const response = data.response || {};
      setDetail({ row, check: response.data || null, canRetry: Boolean(response.canRetry), loading: false });
    } catch (err) {
      console.error('Error cargando el detalle de antecedentes:', err);
      setDetail({ row, check: null, canRetry: false, loading: false, error: true });
    }
  };

  // El filtro manda en la lista y en los lotes: cada lote toma lo que se ve
  const visibleRows = rows.filter((row) => matchesFilter(row, filter));
  const pendingRows = visibleRows.filter(isPending);
  const recheckRows = visibleRows.filter(isRecheckable);

  // Lote secuencial, una llamada por postulante: "Verificar pendientes"
  // (mode 'pending') o "Volver a consultar" (mode 'recheck', consulta nueva)
  const runBulk = async () => {
    const queue = [...(bulk?.queue || [])];
    const mode = bulk?.mode || 'pending';
    const force = mode === 'recheck';
    setBulk({ phase: 'running', mode, queue, total: queue.length, done: 0, ok: 0, failed: 0 });
    let ok = 0;
    let failed = 0;
    for (let i = 0; i < queue.length; i++) {
      // eslint-disable-next-line no-await-in-loop
      const result = await verifyRow(queue[i], { force });
      // Sin puntos (2026-11-01): se detiene; los que faltan quedan sin consultar
      if (result.status === 'points') {
        setBulk({
          phase: 'done', mode, queue, total: queue.length, done: i, ok, failed,
          stopped: pointsBlockMessage(t, result.block), left: queue.length - i,
        });
        return;
      }
      if (result.status === 'ok') ok++; else failed++;
      setBulk({ phase: 'running', mode, queue, total: queue.length, done: i + 1, ok, failed });
    }
    setBulk({ phase: 'done', mode, queue, total: queue.length, done: queue.length, ok, failed });
  };

  // Costo del lote en puntos (2026-11-01): el máximo, y aparte el saldo y
  // cuántos alcanzan si no alcanza para todos (el lote se detiene)
  const confirmBulkText = () => {
    const count = bulk?.queue?.length || 0;
    const opts = { count, total: pts(count * pricePoints), points: pts(pricePoints) };
    if (bulk?.mode !== 'recheck') return tb('confirmBulk', opts);
    if (count === 1) return tb('recheckConfirmOne', { ...opts, name: bulk.queue[0].name || bulk.queue[0].rut || '' });
    return tb('recheckConfirmBulk', opts);
  };
  const confirmBalanceText = () => {
    if (!pointsInfo || pointsInfo.unlimited || typeof pointsInfo.remaining !== 'number') return null;
    const count = bulk?.queue?.length || 0;
    const available = Math.max(0, pointsInfo.remaining);
    const fit = Math.floor((available + 1e-9) / pricePoints);
    return [
      tb('confirmBalance', { available: bal(available) }),
      fit < count ? tb('confirmShort', { fit }) : null,
    ].filter(Boolean).join(' ');
  };
  const verifyTip = pointsBlocked ? pointsBlockedText : t('ats.backgroundCheckPricing.short', { points: pts(pricePoints) });
  const recheckTip = pointsBlocked ? pointsBlockedText : t('ats.backgroundCheckPricing.recheck', { points: pts(pricePoints) });
  const upToTip = (count) => (pointsBlocked
    ? pointsBlockedText
    : t('ats.backgroundCheckPricing.upTo', { total: pts(count * pricePoints), count, points: pts(pricePoints) }));

  const handleOpenApplication = (row) => {
    const application = applications.find((a) => (a.uid || a.id) === row.applicationId);
    if (application && onOpenApplication) {
      onClose();
      onOpenApplication(application);
    }
  };

  const reviewChip = (review) => {
    if (!review) return null;
    const decision = review.decision?.status;
    if (decision === 'approved') return <Chip size="small" label={tb('review.approved')} sx={chipSx('green')} />;
    if (decision === 'rejected') return <Chip size="small" label={tb('review.rejected')} sx={chipSx('red')} />;
    if (review.status === 'requiere_revision') return <Chip size="small" label={tb('review.requiere_revision')} sx={chipSx('yellow')} />;
    return null;
  };

  const renderDetail = () => {
    const { row, check, canRetry, loading: detailLoading, error } = detail;
    const retrying = Boolean(busy[row.applicationId]);
    return (
      <Box>
        <Button size="small" startIcon={<BackIcon fontSize="small" />} onClick={() => setDetail(null)} sx={{ mb: 1 }}>
          {tb('back')}
        </Button>
        <Typography variant="subtitle2" fontWeight={700}>{row.name || '—'}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{row.rut || '—'}</Typography>
        {detailLoading && <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>}
        {error && <Alert severity="error" sx={{ my: 1 }}>{tb('loadError')}</Alert>}
        {!detailLoading && check && (
          <AtsBackgroundCheckSection
            check={check}
            canRetry={canEdit && canRetry}
            retrying={retrying}
            onRetry={() => handleVerifyOne(row)}
            retryPoints={pricePoints}
          />
        )}
        {row.review && (row.review.status === 'requiere_revision' || row.review.decision) && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
              {tb('reviewTitle')}
            </Typography>
            {reviewChip(row.review)}
            {row.review.status === 'requiere_revision' && !row.review.decision && (row.review.reasons || []).map((reason, i) => (
              <Typography key={i} variant="caption" sx={{ display: 'block', color: '#92400E', mt: 0.5 }}>• {reason}</Typography>
            ))}
          </Box>
        )}
        {/* Consulta nueva aunque haya una reciente: quien puede editar la vacante */}
        {canEdit && row.consultable && !detailLoading && (
          <Box sx={{ mb: 2, p: 1.5, border: '1px dashed #D1D5DB', borderRadius: 1 }}>
            {confirmForce ? (
              <>
                <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>{tb('forceRecheckConfirm', { points: pts(pricePoints) })}</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => handleForceRecheck(row)}
                    disabled={retrying}
                    startIcon={retrying ? <CircularProgress size={14} color="inherit" /> : null}
                  >
                    {tb('forceRecheckGo')}
                  </Button>
                  <Button size="small" onClick={() => setConfirmForce(false)} disabled={retrying}>{t('cancel')}</Button>
                </Box>
              </>
            ) : (
              <Tooltip title={recheckTip} arrow describeChild>
                <span>
                  <Button size="small" variant="outlined" startIcon={<RefreshIcon fontSize="small" />} onClick={() => setConfirmForce(true)} disabled={retrying || pointsBlocked}>
                    {tb('forceRecheck')}
                  </Button>
                </span>
              </Tooltip>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{tb('forceRecheckHint')}</Typography>
          </Box>
        )}
        {onOpenApplication && (
          <Button size="small" variant="outlined" startIcon={<OpenIcon fontSize="small" />} onClick={() => handleOpenApplication(row)}>
            {tb('openProfile')}
          </Button>
        )}
      </Box>
    );
  };

  const renderTable = () => (
    <>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        {tb('costNote', { points: pts(pricePoints) })}
        {pointsInfo && !pointsBlocked && (pointsInfo.unlimited || typeof pointsInfo.remaining === 'number') && (
          <Box component="span" sx={{ fontWeight: 600 }}>
            {' '}
            {pointsInfo.unlimited
              ? t('ats.backgroundCheckPricing.unlimited')
              : t('ats.backgroundCheckPricing.available', { points: bal(Math.max(0, pointsInfo.remaining)) })}
          </Box>
        )}
      </Typography>
      {pointsBlocked && canEdit && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>{pointsBlockedText}</Alert>
      )}
      {loading && <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>}
      {loadError && <Alert severity="error" sx={{ mb: 2 }}>{tb('loadError')}</Alert>}
      {!loading && !loadError && rows.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>{tb('empty')}</Typography>
      )}
      {!loading && rows.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.5 }}>
          {FILTERS.map((f) => {
            const active = filter === f;
            const count = rows.filter((row) => matchesFilter(row, f)).length;
            return (
              <Chip
                key={f}
                size="small"
                label={`${tb(`filters.${f}`)} (${count})`}
                variant={active ? 'filled' : 'outlined'}
                onClick={() => { setFilter(f); setBulk(null); }}
                disabled={bulk?.phase === 'running'}
                sx={active ? { backgroundColor: '#6A5AF9', color: '#fff', fontWeight: 600 } : { fontWeight: 500 }}
              />
            );
          })}
        </Box>
      )}
      {!loading && rows.length > 0 && visibleRows.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>{tb('filters.empty')}</Typography>
      )}
      {!loading && visibleRows.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>{tb('columns.name')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{tb('columns.rut')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{tb('columns.status')}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row) => {
              const status = rowStatus(row);
              const action = canEdit ? verifyAction(row) : null;
              const rowBusy = Boolean(busy[row.applicationId]);
              return (
                <TableRow key={row.applicationId}>
                  <TableCell>
                    <Typography variant="body2">{row.name || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{row.rut || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Chip size="small" label={tb(`status.${status}`)} sx={chipSx(STATUS_TONE[status])} />
                      {reviewChip(row.review)}
                    </Box>
                    {status === 'notConsultable' && row.reason && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{tb(`reason.${row.reason}`)}</Typography>
                    )}
                    {row.check?.requestedAt && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {tb('checkedOn', { date: formatDate(row.check.requestedAt) })}
                        {row.check.status !== 'error' && !row.check.reusable ? ` · ${tb('expired')}` : ''}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {row.check && (
                      <Button size="small" onClick={() => openDetail(row)} disabled={rowBusy}>{tb('detail')}</Button>
                    )}
                    {action && (
                      <Tooltip title={verifyTip} arrow describeChild>
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleVerifyOne(row)}
                            disabled={rowBusy || bulk?.phase === 'running' || pointsBlocked}
                            startIcon={rowBusy ? <CircularProgress size={14} color="inherit" /> : null}
                            sx={{ ml: 1, borderColor: '#6A5AF9', color: '#6A5AF9' }}
                          >
                            {rowBusy ? tb('verifying') : tb(action)}
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                    {canEdit && !action && isRecheckable(row) && (
                      <Tooltip title={recheckTip} arrow describeChild>
                        <span>
                          <Button
                            size="small"
                            onClick={() => setBulk({ phase: 'confirm', mode: 'recheck', queue: [row] })}
                            disabled={rowBusy || bulk?.phase === 'running' || pointsBlocked}
                            startIcon={rowBusy ? <CircularProgress size={14} color="inherit" /> : null}
                            sx={{ ml: 1, color: '#6A5AF9' }}
                          >
                            {rowBusy ? tb('verifying') : tb('forceRecheck')}
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      {bulk?.phase === 'confirm' && (
        <Alert
          severity="warning"
          sx={{ mt: 2 }}
          action={(
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" color="inherit" onClick={() => setBulk(null)}>{tb('cancel')}</Button>
              <Button size="small" variant="contained" onClick={runBulk} sx={{ backgroundColor: '#6A5AF9' }}>{tb('confirm')}</Button>
            </Box>
          )}
        >
          {confirmBulkText()}
          {confirmBalanceText() && (
            <Box component="span" sx={{ display: 'block', mt: 0.5, fontWeight: 600 }}>{confirmBalanceText()}</Box>
          )}
        </Alert>
      )}
      {bulk?.phase === 'running' && (
        <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mt: 2 }}>
          {tb('progress', { done: bulk.done, total: bulk.total })}
        </Alert>
      )}
      {bulk?.phase === 'done' && (
        <Alert severity={bulk.failed || bulk.stopped ? 'warning' : 'success'} sx={{ mt: 2 }} onClose={() => setBulk(null)}>
          {bulk.stopped
            ? tb('bulkStopped', { message: bulk.stopped, ok: bulk.ok, left: bulk.left })
            : tb('bulkDone', { ok: bulk.ok, failed: bulk.failed })}
        </Alert>
      )}
    </>
  );

  return (
    <Dialog open={open} onClose={bulk?.phase === 'running' ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{tb('title', { stage: stage?.name || '' })}</DialogTitle>
      <DialogContent dividers>
        {detail ? renderDetail() : renderTable()}
      </DialogContent>
      <DialogActions>
        {!detail && canEdit && filter !== 'consulted' && pendingRows.length > 0 && (!bulk || bulk.phase === 'done') && (
          <Tooltip title={upToTip(pendingRows.length)} arrow describeChild>
            <span style={{ marginRight: 'auto' }}>
              <Button
                variant="contained"
                onClick={() => setBulk({ phase: 'confirm', mode: 'pending', queue: pendingRows })}
                disabled={pointsBlocked}
                sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' } }}
              >
                {tb('verifyPending', { count: pendingRows.length })}
              </Button>
            </span>
          </Tooltip>
        )}
        {!detail && canEdit && filter === 'consulted' && recheckRows.length > 0 && (!bulk || bulk.phase === 'done') && (
          <Tooltip title={upToTip(recheckRows.length)} arrow describeChild>
            <span style={{ marginRight: 'auto' }}>
              <Button
                variant="contained"
                onClick={() => setBulk({ phase: 'confirm', mode: 'recheck', queue: recheckRows })}
                disabled={pointsBlocked}
                sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' } }}
              >
                {tb('recheckBulk', { count: recheckRows.length })}
              </Button>
            </span>
          </Tooltip>
        )}
        <Button onClick={onClose} disabled={bulk?.phase === 'running'}>{tb('close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AtsBackgroundCheckDialog;
